import { Link } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';

const featuredComics = comics.filter((c) => c.featured);
const hotComics = comics.filter((c) => c.hot);
const latestComics = [...comics].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());

export default function HomePage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '皮皮漫画网 - 韩漫在线阅读',
    description: '皮皮漫画网提供韩漫在线阅读和免费韩国漫画下拉式功能，包含全集漫画大全。',
    url: 'https://mousehole.cn',
    isPartOf: {
      '@type': 'WebSite',
      name: '皮皮漫画网',
      url: 'https://mousehole.cn',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20stunning%20stylized%20Korean%20manhwa%20illustration%20collage%20featuring%20various%20comic%20characters%20and%20scenes%20with%20warm%20red%20and%20golden%20accents%2C%20artistic%20brushstroke%20texture%2C%20dramatic%20composition%2C%20rich%20crimson%20and%20amber%20tones%2C%20Korean%20webtoon%20aesthetic%2C%20dynamic%20energy&width=1920&height=800&seq=hero-bg-main&orientation=landscape"
            alt="皮皮漫画网 - 韩漫在线阅读"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
            <h1 className="font-heading text-2xl md:text-5xl font-bold text-background-50 mb-4 max-w-3xl leading-tight">
              韩漫在线阅读，尽在<strong className="text-accent-400">皮皮漫画网</strong>
            </h1>
            <p className="text-sm md:text-lg text-background-100/80 max-w-2xl mb-8 leading-relaxed">
              免费韩国漫画下拉式阅读，全集漫画大全持续更新。从开头到结尾完整追更，无需付费即可畅游漫画世界。
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/category/all"
                className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                开始阅读
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 rounded-full border border-background-50/40 text-background-50 font-medium text-sm hover:bg-background-50/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: '漫画总量', value: '12,580+', icon: 'ri-book-2-line' },
                { label: '每日更新', value: '200+', icon: 'ri-refresh-line' },
                { label: '累计用户', value: '850万+', icon: 'ri-user-line' },
                { label: '好评率', value: '98.6%', icon: 'ri-thumb-up-line' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-4 rounded-lg bg-background-100">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary-100">
                    <i className={`${stat.icon} text-primary-500 text-lg`}></i>
                  </div>
                  <div className="font-heading font-bold text-xl text-foreground-950">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-bold text-xl text-foreground-950">漫画分类</h2>
            <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1 cursor-pointer whitespace-nowrap">
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-5 lg:grid-cols-10 gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200 cursor-pointer group"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 group-hover:bg-primary-100 transition-colors duration-200">
                  <i className={`${cat.icon} text-secondary-600 group-hover:text-primary-500 text-lg transition-colors duration-200`}></i>
                </div>
                <span className="text-xs text-foreground-700 font-medium text-center whitespace-nowrap">{cat.name}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured Comics */}
        <section className="bg-background-100 py-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 rounded-full bg-primary-500"></div>
                <h2 className="font-heading font-bold text-xl text-foreground-950">推荐漫画</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {featuredComics.slice(0, 6).map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-1 h-6 rounded-full bg-accent-500"></div>
              <h2 className="font-heading font-bold text-xl text-foreground-950">最新更新</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {latestComics.slice(0, 6).map((comic) => (
              <ComicCard key={comic.id} {...comic} />
            ))}
          </div>
        </section>

        {/* Hot Comics */}
        <section className="bg-background-100 py-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 rounded-full bg-accent-500"></div>
                <h2 className="font-heading font-bold text-xl text-foreground-950">热门漫画</h2>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {hotComics.slice(0, 6).map((comic) => (
                <ComicCard key={comic.id} {...comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-14">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-2xl text-foreground-950 mb-3">为什么选择皮皮漫画网？</h2>
            <p className="text-sm text-foreground-600 max-w-2xl mx-auto">
              我们致力于为漫画爱好者提供最优质的韩漫在线阅读体验
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-smartphone-line',
                title: '下拉式阅读',
                desc: '专为手机优化的下拉式阅读体验，流畅翻页，让您沉浸在漫画世界中。',
              },
              {
                icon: 'ri-coin-line',
                title: '完全免费',
                desc: '无需付费，所有韩漫资源免费阅读。全集漫画大全持续更新，追更无压力。',
              },
              {
                icon: 'ri-time-line',
                title: '每日更新',
                desc: '每天更新200+话漫画，热门韩国漫画第一时间同步，不错过任何精彩。',
              },
              {
                icon: 'ri-bookmark-line',
                title: '海量资源',
                desc: '收录12,580+部韩漫作品，涵盖恋爱、奇幻、动作、悬疑等多种类型。',
              },
              {
                icon: 'ri-shield-check-line',
                title: '高清画质',
                desc: '所有漫画均提供高清画质，细节清晰，带给您最佳的视觉享受。',
              },
              {
                icon: 'ri-heart-line',
                title: '收藏追更',
                desc: '一键收藏心仪漫画，更新即时提醒，再也不会错过喜欢的作品更新。',
              },
            ].map((feature) => (
              <div key={feature.title} className="p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                  <i className={`${feature.icon} text-primary-500 text-xl`}></i>
                </div>
                <h3 className="font-heading font-semibold text-base text-foreground-950 mb-2">{feature.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative w-full py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20gradient%20abstract%20background%20with%20flowing%20red%20and%20gold%20ink%20wash%20texture%2C%20Korean%20art%20style%2C%20soft%20ambient%20lighting%2C%20elegant%20atmosphere%2C%20rich%20crimson%20and%20amber%20colors&width=1920&height=400&seq=cta-bg-home&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-background-50 mb-4">
              即刻开启您的漫画之旅
            </h2>
            <p className="text-sm text-background-100/80 mb-8">
              加入皮皮漫画网，畅享海量韩漫资源，免费阅读，每日更新！
            </p>
            <Link
              to="/category/all"
              className="inline-flex px-8 py-3 rounded-full bg-accent-500 text-background-50 font-medium text-sm hover:bg-accent-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              立即浏览全部漫画
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}